import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BankAccountsService, BankAccountStreams } from '../../../../services/financial-accounts/bank-accounts.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UnifiedBankAccount } from '../../../../models/entities/unified-bank-account';
import { AddOrgBankAccountDialogComponent, AddOrgBankAccountDialogResult } from '../bank-account-manager/add-org-bank-account-dialog/add-org-bank-account-dialog.component';
import { FinancialConnectionsService } from '../../../../services/stripe-treasury/financial-connections.service';
import { DepositBankAccount } from '../../../../models/entities/deposit-bank-account';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { SharedModule } from '../../../shared/shared.module';

interface PhSearchSelectItem {
    value: string;
    label: string;
    hint?: string;
    badge?: string;
    badgeColor?: string;
}

interface PhSearchSelectToggle {
    key: string;
    label: string;
    checked?: boolean;
}

const ADD_ACCOUNT_KEY = '__add';
const SHOW_DELETED_KEY = 'deleted';

@UntilDestroy()
@Component({
    selector: 'app-bank-account-selector',
    templateUrl: './bank-account-selector.component.html',
    standalone: true,
    imports: [
        // Shared
        SharedModule,
        // Dependencies
        AddOrgBankAccountDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BankAccountSelectorComponent implements OnInit, OnChanges {
    @Input() formGroup: UntypedFormGroup;
    @Input() controlName: string = 'account';
    @Input() organizationId: number = null;
    @Input() accountId: number = null;
    @Input() allowCreate = true;

    // quick filters for selector
    @Input() onlyAlliance = false;
    @Input() onlyDeposit = false;
    @Input() onlyFixedAsset = false;
    @Input() includeDeleted = false;
    @Input() verified = false;
    // Excludes the Treasury-managed "Treasury Balance" account — transactions must
    // originate from a Stripe-backed account, so it is not a valid selection.
    @Input() excludeTreasury = false;

    bankAccountStreams: BankAccountStreams;
    accounts: UnifiedBankAccount[] = [];
    private loadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    $showDeletedAccounts: BehaviorSubject<boolean> = new BehaviorSubject(false);

    @ViewChild('picker') pickerRef: ElementRef<HTMLElement & { value: string | null }>;

    private _addingBankAccount = false;
    addBankAccountOpen = false;

    constructor(
            private _bankAccountsService: BankAccountsService,
            private _fb: UntypedFormBuilder,
            private _financialConnectionsService: FinancialConnectionsService,
            private _changeDetector: ChangeDetectorRef,
    ) {}

    get loading$() {
        return this.loadingSubject$.asObservable();
    }

    get control() {
        return this.formGroup.get(this.controlName) as UntypedFormControl;
    }

    get bankAccounts(): (UnifiedBankAccount|DepositBankAccount)[] {
        let accounts = this.accounts;

        if (this.excludeTreasury) {
            accounts = accounts.filter((uba) => !uba.isTreasuryAccount);
        }

        if (this.onlyAlliance) {
            return accounts.filter((uba) => uba.isAllianceAccount);
        }

        if (this.onlyDeposit) {
            return accounts.filter((uba) => uba.depositBankAccount).map((uba) => uba.depositBankAccount);
        }

        if (this.onlyFixedAsset) {
            return accounts.filter((uba) => uba.fixedAsset);
        }

        if (this.verified) {
            // filter for verified accounts
            return accounts.filter((uba) => uba.lobBankAccounts?.some((lba) => lba.verified));
        }

        return accounts;
    }

    get hasDeletedAccounts(): boolean {
        return this.accounts.some(account => account.deletedAt);
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('includeDeleted' in changes || ('accountId' in changes && this.accountId)) {
            this.loadBankAccounts();
        }
    }

    ngOnInit() {
        if (!this.formGroup) {
            this.formGroup = this._fb.group({
                [this.controlName]: null,
            });
        }

        if (this.organizationId === null) {
            const jwt = LegFiJwtService.read();
            this.organizationId = jwt.orgId;
        }

        this.loadBankAccounts();
    }

    get isLoading(): boolean {
        return this.loadingSubject$.getValue();
    }

    loadBankAccounts() {
        this.loadingSubject$.next(true);

        this.bankAccountStreams = this._bankAccountsService.getOrganizationStreams(this.organizationId);
        let $data = this._bankAccountsService.getOrgBankAccounts(this.organizationId);
        if (this.bankAccountStreams.$loaded.getValue()) {
            $data = this.bankAccountStreams.$bankAccounts.asObservable();
        }

        const activeAccounts$ = $data.pipe(untilDestroyed(this));
        const deletedAccounts$ = this._bankAccountsService.getOrgBankAccounts(this.organizationId, ['depositBankAccount', 'plaidToken', 'fixedAsset', 'reconciliations', 'onlyTrashed'], false).pipe(untilDestroyed(this));

        combineLatest([activeAccounts$, deletedAccounts$]).subscribe({
            next: ([activeAccounts, deletedAccounts]) => {
                this.accounts = [...activeAccounts, ...deletedAccounts];

                if (this.accountId) {
                    const account = this.accounts.find((acct) => acct.id === this.accountId);
                    // Never pre-select an account the exclusion filter hides (e.g. the
                    // "Treasury Balance" account when excludeTreasury is set) — otherwise
                    // the control holds a value not shown in the dropdown.
                    if (account && !(this.excludeTreasury && account.isTreasuryAccount)) {
                        // If the pre-selected account is deleted, enable "Show Deleted" toggle
                        if (account.deletedAt) {
                            this.$showDeletedAccounts.next(true);
                        }
                        this.control.setValue(account, { emitEvent: false });
                    } else {
                        this.control.setValue(null, { emitEvent: false });
                    }
                } else {
                    this.control.setValue(null, { emitEvent: false });
                }
                this.loadingSubject$.next(false);
                // An OnPush host (the V2 return dialog under the payments list) would otherwise keep "Loading accounts…".
                this._changeDetector.markForCheck();
            },
            error: () => {
                this.accounts = [];
                this.loadingSubject$.next(false);
                this._changeDetector.markForCheck();
            },
        });
    }

    get visibleAccounts(): (UnifiedBankAccount | DepositBankAccount)[] {
        const showDeleted = this.$showDeletedAccounts.getValue();
        return this.bankAccounts.filter((account) => !account.deletedAt || showDeleted);
    }

    get items(): PhSearchSelectItem[] {
        const items: PhSearchSelectItem[] = this.allowCreate ? [{value: ADD_ACCOUNT_KEY, label: 'Add New Bank Account'}] : [];
        if (this.isLoading) {
            return items;
        }
        return items.concat(this.visibleAccounts.map((account) => ({
            value: String(account.id),
            label: account.friendlyName,
            hint: maskedNumber(account),
            ...(account.deletedAt ? {badge: 'Deleted', badgeColor: 'red'} : {}),
        })));
    }

    get toggles(): PhSearchSelectToggle[] {
        return this.hasDeletedAccounts
            ? [{key: SHOW_DELETED_KEY, label: 'Show Deleted', checked: this.$showDeletedAccounts.getValue()}]
            : [];
    }

    get selectedKey(): string | null {
        const id = this.formGroup?.get(this.controlName)?.value?.id;
        return id == null ? null : String(id);
    }

    get isEmpty(): boolean {
        return !this.isLoading && !this.allowCreate && this.bankAccounts.length === 0;
    }

    get isDisabled(): boolean {
        return this.isLoading || this.isEmpty || !!this.formGroup?.get(this.controlName)?.disabled;
    }

    get hasError(): boolean {
        const control = this.formGroup?.get(this.controlName);
        return !!control && control.invalid && control.touched;
    }

    get placeholder(): string {
        if (this.isLoading) {
            return 'Loading accounts…';
        }
        if (this.isEmpty) {
            return this.onlyAlliance ? 'No Western Alliance Bank accounts found' : 'No bank accounts found';
        }
        return 'Select bank account';
    }

    onPick(key: string): void {
        const control = this.control;
        if (key === ADD_ACCOUNT_KEY) {
            control.setValue(null);
            if (this.pickerRef?.nativeElement) {
                this.pickerRef.nativeElement.value = null;
            }
            this.addBankAccount();
            return;
        }
        const account = this.visibleAccounts.find((acct) => String(acct.id) === key) ?? null;
        control.setValue(account);
        control.markAsDirty();
        control.markAsTouched();
    }

    onToggle(change: { key: string; checked: boolean }): void {
        if (change?.key === SHOW_DELETED_KEY) {
            this.$showDeletedAccounts.next(change.checked);
        }
    }

    addBankAccount() {
        if (this._addingBankAccount) {
            return;
        }
        this._addingBankAccount = true;

        this._financialConnectionsService.addBankAccount(
                this.organizationId,
                // onStandardDialog fires only for non-Treasury orgs; Treasury orgs route through service-managed KYC/FC flows.
                () => {
                    this.addBankAccountOpen = true;
                },
                () => this.loadBankAccounts(),
                () => { this._addingBankAccount = false; },
        );
    }

    onAddBankAccountClosed(result: AddOrgBankAccountDialogResult) {
        this.addBankAccountOpen = false;
        this._addingBankAccount = false;
        if (result?.success) {
            this.loadBankAccounts();
        }
    }

    comparator(o1: DepositBankAccount, o2: DepositBankAccount) {
        return o1 && o2 && o1.id === o2.id;
    }
}

function maskedNumber(account: UnifiedBankAccount | DepositBankAccount): string | undefined {
    const last4 = 'last4' in account ? account.last4 : account.unifiedBankAccount?.last4;
    if (!last4 || /\*{2,}|•{2,}/.test(account.friendlyName ?? '') || account.friendlyName?.includes(last4)) {
        return undefined;
    }
    return `****${last4}`;
}
